/**
 * frontend/src/seller/message-centre/services/tracking.jsx
 **/
import { trackGtmEvent } from '@gumtree/shared/src/util/gtm-tracker';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import type { AxiosError } from 'axios';
import { MessageUserRoleEnum } from '@gumtree/web-bff/src/api/clients/conversations';
import { listingDetailsFromMessageCentre } from '@gumtree/shared/src/util/ga4-bff';
import { Conversation } from '@gumtree/shared/src/types/message-centre';

export const getBlockEventPayload = (
    userRole: MessageUserRoleEnum
): Pick<GA4.BlockUserEvent, 'event' | 'formName'> => ({
    event: userRole === MessageUserRoleEnum.Seller ? 'block_a_buyer' : 'block_a_seller',
    formName: userRole === MessageUserRoleEnum.Seller ? 'block a buyer' : 'block a seller',
});

export const getDeleteConversationEventPayload = (
    userRole: MessageUserRoleEnum
): Pick<GA4.DeleteConversationEvent, 'event' | 'formName'> => ({
    event:
        userRole === MessageUserRoleEnum.Seller
            ? 'delete_conversation_seller'
            : 'delete_conversation_buyer',
    formName:
        userRole === MessageUserRoleEnum.Seller
            ? 'delete conversation seller'
            : 'delete conversation buyer',
});

export const getReportConverseeEventPayload = (
    userRole: MessageUserRoleEnum
): Pick<GA4.ReportUserEvent, 'event' | 'formName'> => ({
    event: userRole === MessageUserRoleEnum.Seller ? 'report_a_buyer' : 'report_a_seller',
    formName: userRole === MessageUserRoleEnum.Seller ? 'report a buyer' : 'report a seller',
});

export const trackReview = (action: string, reason: string, messages: string[]) => {
    trackGtmEvent({
        action,
        label: { reason, messages },
    });
};

/**
 * When review conditions are
 * met track review bubble
 */
export const trackReviewShowing = () => {
    trackGtmEvent({
        action: 'RatingPromptDisplay',
    });
};

/**
 * When review conditions are
 * met track review bubble
 */
export const trackProfileClick = () => {
    trackGtmEvent({
        action: 'ViewProfileBegin',
    });
};

/**
 * When user clicks on close
 * button within review modal
 */
export const trackModalClose = (granularShow: boolean) => {
    trackGtmEvent({
        action: 'RatingFeedbackClose',
        label: { step: granularShow ? 'two' : 'one' },
    });
};

/**
 * When user clicks on the rating
 * faces track click
 */
export const trackRatingInteraction = (ratingNumber: number) => {
    trackGtmEvent({
        action: 'RatingSelectScore',
        label: { score: ratingNumber },
    });
};

/**
 * When user clicks on the
 * free text box track click
 */
export const trackTextInteraction = () => {
    trackGtmEvent({
        action: 'RatingTypeFeedback',
    });
};

/**
 * When user clicks on the submit
 * button track click
 */
export const trackSubmitPress = (ratingNumber: number) => {
    trackGtmEvent({
        action: 'RatingSubmitScore',
        label: {
            score: ratingNumber,
        },
    });
};

export const ratingSubmitReason = (ratingNumber: number, ratingReasons: string[]) => {
    trackGtmEvent({
        action: 'RatingSubmitReason',
        label: {
            score: ratingNumber,
            reason: ratingReasons,
        },
    });
};

/**
 * When user clicks on the
 * live chat salesforce button
 */
export const trackRatingCSChat = () => {
    trackGtmEvent({
        action: 'RatingChatBegin',
    });
};

export const ratingSelectPositiveReason = (responseType: string) => {
    trackGtmEvent({
        action: 'RatingSelectPositiveReason',
        label: { reason: responseType },
    });
};

export const ratingSelectNegativeReason = (responseType: string) => {
    trackGtmEvent({
        action: 'RatingSelectNegativeReason',
        label: { reason: responseType },
    });
};

//#region block user

export const trackBlockUserOpenModal = () => {
    trackGtmEvent({
        action: 'BlockUserOpenModal',
    });
};

export const trackBlockUserBegin = () => {
    trackGtmEvent({
        action: 'BlockUserBegin',
    });
};

export const trackBlockUserSuccess = (userRole: MessageUserRoleEnum, activeConversation: Conversation) => {
    const { event, formName } = getBlockEventPayload(userRole);

    trackGtmEvent({
        action: 'BlockUserSuccess',
    });

    trackGA4Event<GA4.BlockUserEvent>({
        event,
        formName,
        formStep: 'block submitted',
        formValidation: 'success',
        ...listingDetailsFromMessageCentre(activeConversation),
        blockedUserId: activeConversation.conversee.userId
    });
};

export const trackBlockUserFailure = (userRole: MessageUserRoleEnum, activeConversation: Conversation) => {
    const { event, formName } = getBlockEventPayload(userRole);

    trackGA4Event<GA4.BlockUserEvent>({
        event,
        formName,
        formStep: 'block submitted',
        formValidation: 'failure',
        ...listingDetailsFromMessageCentre(activeConversation),
        blockedUserId: activeConversation.conversee.userId
    });
};

export const trackUnblockUserBegin = () => {
    trackGtmEvent({
        action: 'BlockUserUnblockBegin',
    });
};

export const trackUnblockUserSuccess = () => {
    trackGtmEvent({
        action: 'BlockUserUnblockSuccess',
    });
};

export const trackBlockUserCancel = () => {
    trackGtmEvent({
        action: 'BlockUserCancel',
    });
};

//#endregion

export const trackErrorFetchConversations = (error: AxiosError | Error) => {
    trackGtmEvent({
        action: 'AjaxError',
        label: { error },
    });
};

export const trackErrorFetchActiveConversations = (error: AxiosError | Error) => {
    trackGtmEvent({
        action: 'AjaxError',
        label: { error },
    });
};

export const trackMessageSendAttempt = () => {
    trackGtmEvent({
        action: 'MessageSendAttempt',
    });
};

export const trackMessageSendSuccess = ({
    messageDirection,
    messageTimeLag,
    listingDetails,
    listingDetailsError,
}: Pick<GA4.SendMessageEvent, 'messageTimeLag' | 'messageDirection'> & GA4.BaseListingDetails) => {
    trackGtmEvent({
        action: 'MessageSendSuccess',
    });

    trackGA4Event<GA4.SendMessageEvent>({
        event: 'send_a_message',
        formName: 'buyer & seller conversation',
        formStep: undefined,
        formValidation: 'success',
        formError: undefined,
        messageTimeLag,
        messageDirection,
        listingDetails,
        listingDetailsError,
    });
};

export const trackMessageSendFailure = ({
    messageDirection,
    messageTimeLag,
    listingDetails,
    listingDetailsError,
}: Pick<GA4.SendMessageEvent, 'messageTimeLag' | 'messageDirection'> & GA4.BaseListingDetails) => {
    trackGtmEvent({
        action: 'MessageSendFailure',
    });

    trackGA4Event<GA4.SendMessageEvent>({
        event: 'send_a_message',
        formName: 'buyer & seller conversation',
        formStep: undefined,
        formValidation: 'failure',
        messageTimeLag,
        messageDirection,
        listingDetails,
        listingDetailsError,
    });
};

export const trackDeleteConversation = ({
    userRole,
    success = true,
    listingDetails,
    listingDetailsError,
    conversationId
}: {
    userRole: MessageUserRoleEnum;
    success?: boolean;
    conversationId: string;
} & GA4.BaseListingDetails) => {
    const { event, formName } = getDeleteConversationEventPayload(userRole);

    trackGA4Event<GA4.DeleteConversationEvent>({
        event,
        formName,
        formStep: 'delete conversation submitted',
        formValidation: success ? 'success' : 'failure',
        formError: undefined,
        listingDetails,
        listingDetailsError,
        conversationId,
    });
};

export const trackReportUser = ({
    userRole,
    success = true,
    reasons,
    converseeId,
    listingDetails,
    listingDetailsError,
}: {
    userRole: MessageUserRoleEnum;
    converseeId: number;
    success?: boolean;
    reasons: string[];
} & GA4.BaseListingDetails) => {
    const { event, formName } = getReportConverseeEventPayload(userRole);

    trackGA4Event<GA4.ReportUserEvent>({
        event,
        formName,
        formStep: 'report submitted',
        formValidation: success ? 'success' : 'failure',
        formError: undefined,
        reportReason: reasons,
        reportedUserId: converseeId,
        listingDetails,
        listingDetailsError,
    });
};
